
export type WorkspaceActionKind = 'BOOK_COMPLETE' | 'GENERATION_RUNNING' | 'MISSING_CHAPTER' | 'BOOK_CREDIT_REQUIRED' | 'BUY_CREDIT_REQUIRED' | 'CLEAR_FAILED_LOCK' | 'WRITE_NEXT' | 'PROJECT_REPAIR_REQUIRED';

export interface WorkspaceActionState {
  kind: WorkspaceActionKind;
  buttonLabel: string;
  subLabel: string;
  disabled: boolean;
}

export type RecoveryType = 'ORIGINAL_RESTORED' | 'CACHE_RESTORED' | 'EXPORT_RESTORED' | 'RECONSTRUCTED_REPLACEMENT';

export interface RecoveryMetadata {
  recoveryType: RecoveryType;
  originalRecovered: boolean;
  restoredAt: any;
  restoredByUid: string;
  recoveryOperationId: string;
  sourceDescription: string;
  sourceChapterNumbers?: number[];
  contractVersion?: number;
  contractHash?: string;
  userApproved: boolean;
}

export interface RecoveryRequirement {
  description: string;
  classification: 'REQUIRED' | 'LIKELY' | 'OPTIONAL' | 'FORBIDDEN';
  evidenceSources: Array<{
    sourceType: 'PREVIOUS_CHAPTER' | 'NEXT_CHAPTER' | 'ROADMAP' | 'BLUEPRINT' | 'LEDGER' | 'LATER_CHAPTER';
    chapterNumber?: number;
    evidenceNote: string;
  }>;
  confidence: number;
}

export interface RecoveryContract {
  missingChapterNumber: number;
  recoveryType: 'RECONSTRUCTED_REPLACEMENT';
  requiredOpeningState: Record<string, string>;
  requiredClosingState: Record<string, string>;
  requirements: RecoveryRequirement[];
  unresolvedUncertainties: string[];
  styleRequirements: {
    pov?: string;
    tense?: string;
    tone?: string;
    targetWordCount?: number;
  };
  contractVersion: number;
}

export type RecoveryStatus =
  | 'PENDING'
  | 'CONTEXT_READY'
  | 'CONTRACT_READY'
  | 'CONTRACT_APPROVED'
  | 'GENERATING_PROSE'
  | 'PROSE_READY'
  | 'VALIDATING'
  | 'VALIDATION_FAILED'
  | 'READY_FOR_RESTORE'
  | 'RESTORE_APPROVED'
  | 'RESTORING'
  | 'STORAGE_WRITTEN'
  | 'COMPLETED'
  | 'PARTIAL_FAILURE'
  | 'FAILED'
  | 'CANCELLED';

export interface ValidationReport {
  passed: boolean;
  continuityScore: number;
  styleScore: number;
  openingBridgePassed: boolean;
  closingBridgePassed: boolean;
  violations: Array<{
    severity: 'BLOCKING' | 'WARNING';
    description: string;
    evidence: string[];
  }>;
}

export interface FinalApprovalRecord {
  approvedByUid: string;
  approvedAt: unknown;
  proseHash: string;
  validationHash: string;
  contractHash: string;
  userApproved: true;
}

export interface RecoveryOperation {
  operationId: string;
  projectId: string;
  missingChapterNumber: number;
  ownerUid: string;
  status: RecoveryStatus;
  recoveryType: RecoveryType;
  startedAt: any;
  updatedAt: any;
  contract?: RecoveryContract;
  contractHash?: string;
  contractVersion?: number;
  approvedAt?: any;
  approvedByUid?: string;
  generatedProse?: string;
  generatedTitle?: string;
  proseWordCount?: number;
  proseHash?: string;
  validationHash?: string;
  finalApproval?: FinalApprovalRecord;
  proseStoragePath?: string;
  validationResult?: ValidationReport;
  lastError?: string;
  estimatedModelUsage?: { contractTokens?: number; proseTokens?: number; validateTokens?: number };
  error?: string;
}

export type BlueprintSufficiencyStatus = "ready" | "needs_enrichment" | "not_ready";

export type BlueprintSufficiencyResult = {
  score: number;
  status: BlueprintSufficiencyStatus;
  presentFields: string[];
  weakFields: string[];
  missingFields: string[];
  contradictions: string[];
  recommendedQuestions: string[];
  hardBlockers: string[];
  semanticValidationPassed?: boolean;
};

export type BlueprintFieldSource = "user" | "ai_proposed" | "user_edited" | "approved";

export type BlueprintFieldSourceRecord = {
  source: BlueprintFieldSource;
  updatedAt: string;
  approvedAt?: string;
};

export type BlueprintProposalOperation = {
  fieldPath: string;
  operation: "add" | "strengthen" | "clarify";
  proposedValue: unknown;
  rationale: string;
  userFactsUsed: string[];
  conflictsWithUserFacts: string[];
};

export type BlueprintEnrichmentProposal = {
  proposedFields: Record<string, unknown>;
  operations: BlueprintProposalOperation[];
  rationale: Record<string, string>;
  userFactsPreserved: string[];
  potentialConflicts: string[];
  questionsStillUnanswered: string[];
};

export type BlueprintSourceSnapshot = {
  originalIdea: string;
  originalUserFields: Partial<StoryBible>;
  createdAt: string;
};

export interface StoryStructure {
  protagonistIdentity?: string;
  protagonistGoal?: string;
  internalLie?: string;
  emotionalNeed?: string;
  primaryOpposition?: string;
  centralConflict?: string;
  stakes?: string;
  incitingIncident?: string;
  midpointReversal?: string;
  majorDefeat?: string;
  crisisChoice?: string;
  climax?: string;
  endingTransformation?: string;
  relationshipArc?: string;
  pov?: string;
  tense?: string;
  [key: string]: string | undefined;
}


export enum AgentRole {
  ARCHITECT = 'Architect',
  SCRIBE = 'Scribe',
  EDITOR = 'Editor',
  PUBLICATION_EDITOR = 'PublicationEditor',
  ARCHIVIST = 'Archivist',
  INSPECTOR = 'Inspector',
  REWRITER = 'Rewriter',
}

export enum StoryMode {
  NOVEL = 'NOVEL',
  SHORT_STORY = 'SHORT_STORY',
  COMIC_SCRIPT = 'COMIC_SCRIPT',
}

export enum ChapterLength {
  MICRO = 'MICRO',
  STANDARD = 'STANDARD',
  EXTENDED = 'EXTENDED',
}

export enum AppStatus {
  DASHBOARD = 'DASHBOARD',
  INITIALIZING = 'INITIALIZING',
  SETUP = 'SETUP',
  PLANNING = 'PLANNING',
  BLUEPRINT_REVIEW = 'BLUEPRINT_REVIEW',
  IDLE = 'IDLE',
  RETRIEVING = 'RETRIEVING',
  SCRIBING = 'SCRIBING',
  EDITING = 'EDITING',
  PUBLISHING = 'PUBLISHING',
  ARCHIVING = 'ARCHIVING',
  INSPECTING = 'INSPECTING',
  REWRITING = 'REWRITING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

export type GenreWeight = 'primary' | 'secondary' | 'accent';

export interface WeightedGenre {
  name: string;
  weight: GenreWeight;
}

export interface NarrativePremise {
  surface: string; // The visible conflict
  tension: string; // Why it matters / Thematic glue
  change: string; // What cannot go back / Irreversible change
}

export interface ChapterBeat {
  type: "exact facts" | "required outcomes" | "optional staging" | "emotional payoff only" | "forbidden repeat";
  description: string;
}

export type DescriptionDensityPreference = 'lean' | 'balanced' | 'lush';
export type ProceduralDetailPreference = 'compressed' | 'moderate' | 'technical';
export type PacingPreference = 'fast' | 'balanced' | 'deliberate';
export type DialoguePreference = 'low' | 'balanced' | 'high';

export interface ProseControls {
  descriptionDensity: DescriptionDensityPreference;
  proceduralDetail: ProceduralDetailPreference;
  pacing: PacingPreference;
  dialoguePreference: DialoguePreference;
}

export interface NarrativeMotionContract {
  viewpointCharacter: string;
  immediateGoal: string;
  meaningfulObstacle: string;
  decisionRequired: string;
  consequence: string;
  stateChange: string;
  disruptionDeadlinePercent: number;
  maxSetupPercent: number;
  maxRoutineProcedureBeats: number;
  requiredEscalations: number;
  sceneQuestion: string;
  sceneAnswerOrComplication: string;
}

export interface NarrativeDensityAudit {
  descriptionPercent: number;
  routineProcedurePercent: number;
  dialoguePercent: number;
  internalConflictPercent: number;
  consequentialActionPercent: number;
  expositionPercent: number;
  firstDisruptionPositionPercent: number;
  meaningfulStateChanges: number;
  repeatedActionClusters: string[];
  /** Legacy telemetry — entity recurrence and non-blocking hints only */
  overdescribedObjects: string[];
  repetitionFindings?: RepetitionEvidence[];
  repetitionSeverity?: 'PASS' | 'WARNING' | 'BLOCKING';
  lowValueProcedureSequences: string[];
  warnings?: string[];
  chapterNumber?: number;
}

export type RepetitionSeverity = 'INFO' | 'WARNING' | 'BLOCKING';

export type RepetitionReason =
  | 'REPEATED_ATTRIBUTE'
  | 'NEAR_DUPLICATE_SENTENCE'
  | 'REPEATED_MECHANICAL_EXPLANATION'
  | 'REDUNDANT_SENSORY_DESCRIPTION'
  | 'REPEATED_ACTION_PATTERN'
  | 'ENTITY_RECURRENCE';

export interface RepetitionEvidence {
  term: string;
  excerpts: string[];
  paragraphIndexes: number[];
  similarityScore?: number;
  reason: RepetitionReason;
  severity: RepetitionSeverity;
}

export interface RepetitionContext {
  characterNames: string[];
  aliases: string[];
  locationNames: string[];
  factionNames: string[];
  requiredEntities: string[];
  criticalObjects: string[];
}

export interface MomentumEvaluationDimensions {
  continuity: 'PASS';
  momentum: 'PASS' | 'WARNING' | 'FAIL';
  repetition: 'PASS' | 'WARNING' | 'FAIL';
  overall: 'PASS' | 'FAIL';
}

export interface MomentumCorrectionOrder {
  delayedDisruption?: boolean;
  excessiveDescription?: string[];
  repetitionEvidence?: RepetitionEvidence[];
  proceduralPadding?: string[];
  repeatedAction?: string[];
  weakSceneChange?: string[];
  lateSceneStart?: string[];
  extendedSceneEnding?: string[];
  requiredRevision: string;
  targetReductionPercent?: number;
  targetedLineEdits?: string[];
}

export type ExpansionDeficitStrategy = 'MINOR' | 'MAJOR';

export type ExpansionPassStatus =
  | 'SUCCESS'
  | 'EXPANSION_INSUFFICIENT_GROWTH'
  | 'TRUNCATED'
  | 'FAILED';

export interface SceneExpansionAllocation {
  sceneId: string;
  currentWords: number;
  targetAdditionalWords: number;
  dramaticPurpose: string;
  allowedExpansionMethods: string[];
  forbiddenPaddingMethods: string[];
  insertionAnchor?: string;
}

export interface ChapterExpansionPlan {
  currentWordCount: number;
  targetWordCount: number;
  minimumAcceptedWords: number;
  targetWordRangeMin: number;
  targetWordRangeMax: number;
  requiredAdditionalWords: number;
  deficitStrategy: ExpansionDeficitStrategy;
  minimumUsefulGrowth: number;
  sceneExpansions: SceneExpansionAllocation[];
}

export interface SceneExpansionInsertion {
  sceneId: string;
  afterAnchor: string;
  passage: string;
  narrativeFunction: string;
}

export interface ExpansionOutputMetadata {
  requestedTargetWords: number;
  outputWords: number;
  finishReason?: string;
  outputTokenCount?: number;
  truncated: boolean;
  model?: string;
}

export interface ExpansionPassResult {
  status: ExpansionPassStatus;
  addedWords: number;
  previousWordCount: number;
  newWordCount: number;
  minimumUsefulGrowth: number;
  metadata?: ExpansionOutputMetadata;
  reason?: string;
}

export interface ChapterContractSufficiency {
  sufficient: boolean;
  sceneTurnCount: number;
  hasConflict: boolean;
  hasDecision: boolean;
  hasConsequence: boolean;
  hasInteractionOrDilemma: boolean;
  hasEndingChange: boolean;
  issues: string[];
  suggestedBeats: string[];
}

export interface PreservedSourceMaterial {
  canonEvents: string[];
  usefulDialogue: string[];
  decisions: string[];
  discoveries: string[];
  sceneOutcomes: string[];
  establishedImagery: string[];
}

export interface MajorChapterBuildScene {
  sceneId: string;
  sequence: number;
  povCharacter: string;
  location: string;
  openingState: string;
  dramaticGoal: string;
  obstacle: string;
  decision: string;
  consequence: string;
  requiredBeats: string[];
  targetMinWords: number;
  targetMaxWords: number;
  acceptanceFloorWords: number;
  continuityInputs: string[];
  forbiddenPadding: string[];
  preservedMaterialRefs: string[];
}

export interface MajorChapterBuildPlan {
  chapterNumber: number;
  minimumAcceptedWords: number;
  preferredTargetWords: number;
  sourceDraftWords: number;
  requiredBeats: string[];
  preservedSourceMaterial: PreservedSourceMaterial;
  scenes: MajorChapterBuildScene[];
}

export type DramaticBeatFunction =
  | 'OPENING_PRESSURE'
  | 'ESCALATION'
  | 'DECISION_REVERSAL'
  | 'CONSEQUENCE_HOOK';

export type AtomicChapterEventSource = 'ROADMAP' | 'ENDING_TURN' | 'CHAPTER_PURPOSE';

export interface AtomicChapterEvent {
  eventId: string;
  sequenceHint: number;
  description: string;
  requiredOutcome: string;
  source: AtomicChapterEventSource;
  primaryBeatId: string;
}

export interface DramaticChapterBeat {
  beatId: string;
  sequence: 1 | 2 | 3 | 4;
  function: DramaticBeatFunction;
  location: string;
  openingState: string;
  dramaticGoal: string;
  obstacle: string;
  conflict: string;
  requiredEvents: string[];
  /** Stable atomic event IDs this beat must primarily dramatize. */
  primaryEventIds: string[];
  acceptanceFloorWords: number;
  dialogueOpportunity: string;
  characterDecision: string;
  immediateConsequence: string;
  endingState: string;
  transitionToNextBeat: string;
  targetMinWords: number;
  targetMaxWords: number;
  continuityInputs: string[];
  forbiddenPadding: string[];
}

export interface FourBeatChapterContract {
  chapterNumber: number;
  chapterTitle: string;
  minimumAcceptedWords: number;
  preferredTargetWords: number;
  preferredTargetRangeMin: number;
  preferredTargetRangeMax: number;
  povCharacter: string;
  chapterOpeningState: string;
  chapterEndingState: string;
  requiredRoadmapBeats: string[];
  atomicEvents: AtomicChapterEvent[];
  continuityConstraints: string[];
  forbiddenEvents: string[];
  beats: [DramaticChapterBeat, DramaticChapterBeat, DramaticChapterBeat, DramaticChapterBeat];
}

export interface GeneratedChapterBeat {
  beatId: string;
  sequence: number;
  content: string;
  endingState: string;
  wordCount: number;
  requiredEventsSatisfied: string[];
  decisionPresent: boolean;
  consequencePresent: boolean;
  finishReason?: string;
  outputTokenCount?: number;
  truncated: boolean;
}

export interface GeneratedFourBeatChapter {
  chapterId: number;
  title: string;
  beats: GeneratedChapterBeat[];
  summary: string;
  content: string;
  totalWordCount: number;
}

export interface FourBeatValidationIssue {
  beatId: string;
  sequence: number;
  code:
    | 'UNDER_WORD_MINIMUM'
    | 'BELOW_PREFERRED_TARGET'
    | 'MISSING_REQUIRED_EVENT'
    | 'MISSING_DECISION'
    | 'MISSING_CONSEQUENCE'
    | 'TRUNCATED'
    | 'SUMMARY_LIKE'
    | 'POSSIBLE_SUMMARY_COMPRESSION'
    | 'NO_STATE_CHANGE'
    | 'REPEATED_INTRODUCTION'
    | 'MID_SENTENCE_END'
    | 'NEARLY_EMPTY'
    | 'MISSING_BEAT'
    | 'DUPLICATE_BEAT_ID'
    | 'UNKNOWN_BEAT_ID'
    | 'WRONG_BEAT_COUNT';
  message: string;
  severity: 'BLOCK' | 'REPAIR' | 'WARNING';
}

export type GeneratedBeatStatus = 'ACCEPTED' | 'REPAIR_REQUIRED' | 'BLOCKED';

export type FourBeatResponseFormat = 'FOUR_BEAT_V1' | 'LEGACY_SCENES_IMPORT';

export type FourBeatGenerationArchitecture = 'FOUR_BEAT';

/** Set by the current four-beat generator; not inferred from manuscript separators. */
export interface FourBeatChapterProvenance {
  responseFormat: 'FOUR_BEAT_V1';
  generationArchitecture: FourBeatGenerationArchitecture;
}

export const FOUR_BEAT_V1_PROVENANCE: FourBeatChapterProvenance = {
  responseFormat: 'FOUR_BEAT_V1',
  generationArchitecture: 'FOUR_BEAT',
};

export interface ClassifiedChapterBeat extends GeneratedChapterBeat {
  status: GeneratedBeatStatus;
  issues: FourBeatValidationIssue[];
}

export interface FourBeatGateResult {
  accepted: boolean;
  structurallyValid: boolean;
  allBeatsAccepted: boolean;
  allFourBeatsPresent: boolean;
  allBeatIdsUnique: boolean;
  beats: ClassifiedChapterBeat[];
  issues: FourBeatValidationIssue[];
  warnings: FourBeatValidationIssue[];
  repairIssues: FourBeatValidationIssue[];
  belowPreferredBeatIds: string[];
  route: 'INSPECTOR' | 'REPAIR' | 'BLOCKED';
  repairCallsUsed: number;
  preInspectorRepairedBeatIds: string[];
  blockedReason?: string;
}

export interface FourBeatChapterValidationResult {
  valid: boolean;
  issues: FourBeatValidationIssue[];
  deficientBeatIds: string[];
  beatWordCounts: number[];
  totalWordCount: number;
  structurallyValid: boolean;
  allFourBeatsPresent: boolean;
  allBeatIdsUnique: boolean;
  beatStatuses: ClassifiedChapterBeat[];
}

export interface FourBeatGenerationDiagnostic {
  scribeOperation: 'INITIAL_CHAPTER' | 'INSPECTOR_REWRITE';
  thinkingLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  chapterNumber: number;
  beatCount: number;
  beatBudgets: Array<{ beatId: string; targetMinWords: number; targetMaxWords: number }>;
  beatWordCounts: number[];
  totalWordCount: number;
  finishReason?: string;
  outputTokenCount?: number;
  truncated: boolean;
  validationIssues?: FourBeatValidationIssue[];
}

export interface FourBeatGenerationOptions {
  onUserProgress?: (message: string) => void;
  onDeveloperDiagnostic?: (diagnostic: FourBeatGenerationDiagnostic) => void;
  onFourBeatSession?: (session: import('../utils/fourBeatInspectorOrchestration.service').FourBeatChapterSession) => void;
  fourBeatRunContext?: {
    projectId: string;
    runId: string;
  };
  /** Shared authoritative continuity snapshot for Scribe and beat repair. */
  authoritativeContinuitySnapshot?: AuthoritativeContinuitySnapshot;
  /** Explicit legacy/import compatibility only — never for live INITIAL_CHAPTER. */
  responseFormat?: FourBeatResponseFormat;
}

export type InspectorVerdict = 'PASS' | 'REPAIR_REQUIRED' | 'BLOCKED';

export type InspectorAuthoritativeBeatId = 'beat-1' | 'beat-2' | 'beat-3' | 'beat-4';

export interface InspectorBeatRepairRequest {
  beatId: InspectorAuthoritativeBeatId;
  reasonCodes: string[];
  explanation: string;
}

export class FourBeatStructuralFailureError extends Error {
  readonly chapterNumber: number;
  readonly gate: FourBeatGateResult;
  readonly partialContent?: string;

  constructor(message: string, params: { chapterNumber: number; gate: FourBeatGateResult; partialContent?: string }) {
    super(message);
    this.name = 'FourBeatStructuralFailureError';
    this.chapterNumber = params.chapterNumber;
    this.gate = params.gate;
    this.partialContent = params.partialContent;
  }
}

export type FourBeatGenerationPolicy = 'four_beat_initial' | 'four_beat_repair';

export interface FourBeatTransportDiagnostic {
  generationPolicy: FourBeatGenerationPolicy;
  logicalCallId: string;
  chapterNumber: number;
  beatId?: string;
  narrativeAttempt: number;
  transportAttempt: number;
  serverUpstreamAttempt: number;
  scribeOperation: 'INITIAL_CHAPTER' | 'INSPECTOR_REWRITE';
  thinkingLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

export class FourBeatCallBudgetExhaustedError extends Error {
  readonly chapterNumber: number;
  readonly reason: 'logical_calls' | 'upstream_attempts';
  readonly logicalCallsUsed: number;
  readonly upstreamAttemptsUsed: number;

  constructor(
    message: string,
    params: {
      chapterNumber: number;
      reason: 'logical_calls' | 'upstream_attempts';
      logicalCallsUsed: number;
      upstreamAttemptsUsed: number;
    },
  ) {
    super(message);
    this.name = 'FourBeatCallBudgetExhaustedError';
    this.chapterNumber = params.chapterNumber;
    this.reason = params.reason;
    this.logicalCallsUsed = params.logicalCallsUsed;
    this.upstreamAttemptsUsed = params.upstreamAttemptsUsed;
  }
}

export interface GeneratedExpansionScene {
  sceneId: string;
  content: string;
  summary: string;
  endingState: string;
  wordCount: number;
  requiredBeatsSatisfied: string[];
  decisionPresent: boolean;
  consequencePresent: boolean;
  finishReason?: string;
  outputTokenCount?: number;
  truncated: boolean;
}

export interface ScribeSceneDiagnostic {
  scribeOperation: string;
  thinkingLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  sceneId?: string;
  targetMinWords?: number;
  targetMaxWords?: number;
  acceptanceFloorWords?: number;
  outputWords?: number;
  requiredBeatsSatisfied?: string[];
  finishReason?: string;
  outputTokenCount?: number;
  truncated?: boolean;
  attemptNumber?: number;
  logicalCallNumber?: number;
  logicalSceneCallId?: string;
  sceneAttempt?: number;
  transportAttempt?: number;
  serverUpstreamAttempt?: number;
  generationPolicy?: string;
}

export type MajorSceneOperation =
  | 'major_scene_build'
  | 'major_scene_retry'
  | 'small_gap_fill'
  | 'weakest_scene_regeneration';

export interface SceneTransportDiagnostic {
  logicalSceneCallId: string;
  sceneId: string;
  sceneAttempt: number;
  transportAttempt: number;
  serverUpstreamAttempt: number;
  generationPolicy: 'major_scene';
  operation: MajorSceneOperation;
}

export type MajorBuildProgressKind =
  | 'scene_build_start'
  | 'scene_complete'
  | 'scene_retry'
  | 'assembled'
  | 'gap_fill'
  | 'weakest_regen';

export interface MajorBuildProgressEvent {
  kind: MajorBuildProgressKind;
  message: string;
  sceneId?: string;
  sceneSequence?: number;
  wordCount?: number;
  assembledWordCount?: number;
}

export interface MajorBuildExecutionResult {
  title: string;
  summary: string;
  content: string;
  assembledWordCount: number;
  scenes: GeneratedExpansionScene[];
  logicalCallsUsed: number;
  gapFillApplied: boolean;
  weakestSceneRegenerated: boolean;
  belowMinimumAfterRepair: boolean;
}

export type ScribeOperation =
  | 'INITIAL_CHAPTER'
  | 'MAJOR_SCENE_BUILD'
  | 'MAJOR_SCENE_RETRY'
  | 'INSPECTOR_REWRITE'
  | 'MINOR_INSERTION'
  | 'SMALL_GAP_FILL';

export type ChapterPlanSource =
  | 'USER_ROADMAP'
  | 'AI_GENERATED'
  | 'AI_REGENERATED'
  | 'AI_ROADMAP'
  | 'PARTIAL_IDEAS'
  | 'IMPORTED_MANUSCRIPT'
  | 'LEGACY';

export interface ChapterPlan {
  id: string;
  chapterNumber: number;
  title: string;
  pov?: string;
  summary: string;
  requiredBeats: string[];
  forbiddenMoves?: string[];
  continuityNotes?: string[];
  characterFocus?: string[];
  locationFocus?: string[];
  narrativeMotion?: NarrativeMotionContract[];
  source?: ChapterPlanSource;
  approvedAt?: string;
  status: "planned" | "drafted" | "revised" | "locked";
  manuscriptChapterId?: string;
  updatedAt: string;
}

export interface RoadmapVersion {
  id: string;
  projectId: string;
  createdAt: string;
  reason: "initial_generation" | "manual_edit" | "ai_regeneration" | "reorder" | "chapter_insert" | "chapter_delete";
  chapterPlans: ChapterPlan[];
  warnings?: RoadmapWarning[];
}

export interface Chapter {
  id: number;
  chapterNumber?: number;
  title: string;
  content?: string; // Optional because we might offload to storage
  summary: string;
  status?: string;
  wordCount?: number;
  canonStatus?: string;
  storagePath?: string;
  createdAt?: string;
  updatedAt?: string;
  editedContent?: string;
  editedByUser?: boolean;
  lastEditedAt?: string;
  contentSource?: 'inline' | 'storage';
  storageContentVerified?: boolean;
  recoveryMetadata?: RecoveryMetadata;
  /** Present only when content was produced by the current four-beat orchestrator. */
  fourBeatProvenance?: FourBeatChapterProvenance;
}

export interface CanonRecordMetadata {
  projectId: string;
  canonId?: string;
  seriesId?: string;
  volumeNumber?: number;
  sourceChapterId?: number;
  sourceType?: string;
  createdAt: string;
  canonStatus?: 'active' | 'resolved' | 'historical' | 'forbidden_current';
}

export interface CanonRecord<T> extends CanonRecordMetadata {
  content: T;
}

export type PhysicalObjectType =
  | 'VEHICLE'
  | 'WEAPON'
  | 'TOOL'
  | 'ARTIFACT'
  | 'MACHINE'
  | 'ANIMAL_EQUIPMENT'
  | 'CONTAINER'
  | 'OTHER';

export type PhysicalObjectOperationalStatus =
  | 'OPERATIONAL'
  | 'DAMAGED'
  | 'DISABLED'
  | 'DESTROYED'
  | 'ABANDONED'
  | 'MISSING'
  | 'UNKNOWN';

export type PhysicalObjectMobilityStatus =
  | 'MOBILE'
  | 'IMMOBILE'
  | 'TOWED'
  | 'CARRIED'
  | 'LEFT_BEHIND'
  | 'UNKNOWN';

export type PhysicalContinuityImportance = 'BACKGROUND' | 'RELEVANT' | 'REQUIRED_CARRYOVER';

export type PhysicalContinuityObligationType =
  | 'EXPLAIN_LOCATION'
  | 'EXPLAIN_ABANDONMENT'
  | 'EXPLAIN_REPAIR'
  | 'EXPLAIN_TRANSFER'
  | 'EXPLAIN_DESTRUCTION'
  | 'EXPLAIN_RECOVERY';

export type PhysicalContinuityObligationStatus = 'OPEN' | 'CARRIED_FORWARD' | 'RESOLVED';

export type InspectorPhysicalStateChangeType =
  | 'INTRODUCED'
  | 'MOVED'
  | 'DAMAGED'
  | 'DISABLED'
  | 'REPAIRED'
  | 'TRANSFERRED'
  | 'ABANDONED'
  | 'DESTROYED'
  | 'CARRIED_FORWARD';

export type InspectorPhysicalConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

export interface InspectorPhysicalStateChange {
  candidateId: string;
  canonicalName: string;
  objectType: PhysicalObjectType;
  changeType: InspectorPhysicalStateChangeType;
  ownerOrHolder?: string;
  location?: string;
  unresolvedAtChapterEnd: boolean;
  beatId: string;
  confidence: InspectorPhysicalConfidence;
}

export interface ContinuityObjectState {
  objectId: string;
  canonicalName: string;
  aliases: string[];
  objectType: PhysicalObjectType;
  ownerCharacterIds: string[];
  currentHolderCharacterId?: string;
  currentLocationId?: string;
  currentLocationDescription?: string;
  operationalStatus: PhysicalObjectOperationalStatus;
  mobilityStatus?: PhysicalObjectMobilityStatus;
  lastSeenChapter: number;
  lastChangedChapter: number;
  sourceChapterId: string;
  sourceRevisionId?: string;
  continuityImportance: PhysicalContinuityImportance;
  unresolvedObligation?: string;
}

export interface PhysicalContinuityObligation {
  obligationId: string;
  objectId: string;
  originatingChapter: number;
  status: PhysicalContinuityObligationStatus;
  obligation: PhysicalContinuityObligationType;
  description: string;
  requiredByChapter?: number;
}

export interface Ledger {
  characters: Record<string, any & Partial<CanonRecordMetadata>>;
  inventory: Array<string | CanonRecord<string>>;
  locations: Record<string, any & Partial<CanonRecordMetadata>>;
  current_time: string;
  plot_points: Array<string | CanonRecord<string>>;
  resolved_plot_points?: Array<string | CanonRecord<string>>;
  momentum: string;
  arc_stage: string;
  last_updated: string;
  historical_events?: Array<string | CanonRecord<string>>;
  continuity_objects?: ContinuityObjectState[];
  physical_continuity_obligations?: PhysicalContinuityObligation[];
  /** Structured delta from the most recently accepted chapter. */
  latestStateDelta?: LatestStateDelta;
  relationships?: Record<string, any & Partial<CanonRecordMetadata>>;
  factions?: Record<string, any & Partial<CanonRecordMetadata>>;
  world_rules?: Array<string | CanonRecord<string>>;
  state_records?: Array<string | CanonRecord<string>>;
  /** Ledger schema revision for additive merge behavior. */
  ledgerSchemaVersion?: number;
}

export interface LogEntry {
  id: string;
  timestamp: string;
  agent: AgentRole | 'System';
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'action';
  audience?: 'user' | 'developer';
  technicalMessage?: string;
  requestId?: string;
}

export interface PublicationEditorResult {
  patched_content: string;
  change_log: { issue: string; fix: string; location: string }[];
  lore_compliance: { rail: string; status: 'PASS' | 'FAIL'; note?: string }[];
  architect_queries?: string[];
}

export interface ValidationResult {
  status: 'PASS' | 'FAIL';
  verdict?: InspectorVerdict;
  beatRepairRequests?: InspectorBeatRepairRequest[];
  rejectedBeatRepairIds?: string[];
  physicalStateChanges?: InspectorPhysicalStateChange[];
  reason?: string;
  correction_order?: string;
  momentum_audit?: NarrativeDensityAudit;
  momentum_correction?: MomentumCorrectionOrder;
}

export interface SoftStateBrief {
  recentSensoryCarryover: string;
  activeEmotionalResidue: string;
  voiceNotes: string;
  environmentalContinuityCues: string;
  motifsToLightlyPreserve: string;
  antiRepetitionWarnings: string;
}

export interface EditorResult {
  status: 'APPROVED' | 'REJECTED';
  feedback?: string;
  polished_text?: string;
}

export interface ContentBoundaries {
  audience?: 'Adult' | 'New Adult' | 'Young Adult' | 'Middle Grade';
  romanceHeat?: 'None' | 'Sweet Romance' | 'Closed-Door Romance' | 'Open-Door Romance' | 'Spicy Romance';
  violenceLevel?: 'Low' | 'Moderate' | 'Graphic';
  language?: 'Clean' | 'Moderate' | 'Strong';
  darkThemes?: 'None' | 'Moderate' | 'Heavy';
}


export interface EraState {
  currentVolume: number;
  yearsAfterPreviousVolume?: number;
  yearsAfterSeriesStart?: number;
  currentEra: string;
  oldSystem: string;
  currentSystem: string;
  resolvedSystems: string[];
  activeSystems: string[];
  forbiddenEraConfusions: string[];
}

export interface ConceptBudget {
  maxMajorNewSystemsPerVolume: number;
  majorNewSystems: string[];
  secondaryConcepts: string[];
  derivedConcepts: string[];
}

export interface RoadmapWarning {
  severity: "warning";
  type: string;
  message: string;
  affectedChapters?: number[];
  userChoice?: "Accept" | "Edit" | "Regenerate";
}

export interface RoadmapChunkRecord {
  chunkIndex: number;
  startChapter: number;
  endChapter: number;
  requestId?: string;
  plans: ChapterPlan[];
  validatedAt: string;
  status: 'valid' | 'invalid';
  errorCode?: string;
}

export interface RoadmapPlanningState {
  targetChapterCount: number;
  batchSize: number;
  chunks: RoadmapChunkRecord[];
  phase: 'pending' | 'chunking' | 'assembling' | 'complete' | 'failed';
  lastError?: string;
  updatedAt: string;
}

export interface StoryBible {
  endingType?: string;
  title: string;
  roadmapWarnings?: RoadmapWarning[];
  roadmapPlanning?: RoadmapPlanningState;
  seriesTitle?: string;
  volumeTitle?: string;
  authorName?: string;
  subtitle?: string;
  eraState?: EraState;
  conceptBudget?: ConceptBudget;
  seriesId?: string;
  canonId?: string;
  genre: WeightedGenre[];
  contentBoundaries?: ContentBoundaries;
  premise: NarrativePremise;
  style_guide: string;
  mode: StoryMode;
  targetChapterCount: number;
  chapterLength: ChapterLength;
  isClosingMode?: boolean;
  volume: number;
  chapterPlans?: ChapterPlan[];
  premadeRoadmap?: string;
  activeRoadmapSource?: ChapterPlanSource;
  originalUserRoadmapRaw?: string;
  originalUserRoadmapParsed?: ChapterPlan[];
  roadmapParseWarnings?: string[];
  isBlueprintApproved?: boolean;
  isRoadmapApproved?: boolean;
  roadmapApprovedAt?: string;
  canonLocks?: string | string[];
  styleRules?: Record<string, any>;
  blueprintSufficiency?: BlueprintSufficiencyResult;
  blueprintApproved?: boolean;
  blueprintApprovedAt?: string;
  blueprintVersion?: number;
  blueprintFieldSources?: Record<string, BlueprintFieldSourceRecord>;
  blueprintSourceSnapshot?: BlueprintSourceSnapshot;
  blueprintPipelineVersion?: string;
  requireBlueprintApproval?: boolean;
  storyStructure?: StoryStructure;
  creditTier?: 'free_preview' | 'short_manuscript' | 'standard_manuscript' | 'large_manuscript' | 'epic_manuscript';
  rewritesUsed?: number;
  paidEntitlementId?: string;
  entitlementAppliedAt?: string;
  maxChaptersAllowed?: number;
  selectedChapterCount?: number;
  rewriteCreditsIncluded?: number;
  exportUnlocked?: boolean;
  hasUnsyncedEdits?: boolean;
  sequelPlan?: SequelPlanConfig;
  proseControls?: Partial<ProseControls>;
  continuityEngineVersion?: ContinuityEngineVersion;
}

export type SequelPlanWorkflowStatus =
  | 'DRAFT'
  | 'PROPOSAL_GENERATED'
  | 'CANON_REVIEWED'
  | 'APPROVED'
  | 'CREATING'
  | 'CREATED';

export type SequelStoryStructureMode =
  | 'chronovela_roadmap'
  | 'paste_full'
  | 'partial_ideas'
  | 'lock_events'
  | 'first_five_only';

export type AiProposalFieldStatus = 'pending' | 'accepted' | 'rejected' | 'edited';

export interface AiProposalFieldState {
  value: string;
  source: 'ai' | 'user';
  status: AiProposalFieldStatus;
}

export interface SequelAuthorVision {
  authorDirection: string;
  mainConflictDirection: string;
  focusCharacters: string[];
  desiredEnding: string;
  themesAndEmotionalDirection: string;
  mustHappen: string[];
  mustNotHappen: string[];
  charactersToFeatureMore: string[];
  charactersToFeatureLess: string[];
  toneDirection: string;
  pacingDirection: string;
  newStoryElementsNotes: string;
  additionalInstructions: string;
}

export interface ChapterAnchor {
  chapterNumber: number;
  requiredEvent: string;
  locked: boolean;
}

export interface SequelStoryStructureChoice {
  mode: SequelStoryStructureMode;
  premadeRoadmap?: string;
  partialIdeas?: string;
  chapterAnchors?: ChapterAnchor[];
}

export interface SequelAiProposal {
  surface: AiProposalFieldState;
  tension: AiProposalFieldState;
  change: AiProposalFieldState;
  approved: boolean;
  declinedExplicitly: boolean;
}

export interface SequelLedgerInjection {
  type: 'character' | 'location' | 'faction' | 'artifact' | 'creature' | 'world_rule' | 'plot_requirement';
  name: string;
  description: string;
}

export interface SequelPlanConfig {
  workflowStatus: SequelPlanWorkflowStatus;
  authorVision: SequelAuthorVision;
  aiProposal: SequelAiProposal;
  storyStructure: SequelStoryStructureChoice;
  ledgerInjections?: SequelLedgerInjection[];
  canonReviewedAt?: string;
  planApprovedAt?: string;
}

export type SequelSeriesStateLoadStatus = 'READY' | 'INCOMPLETE' | 'FAILED';

export interface SequelSeriesStateCharacter {
  id?: string;
  name: string;
  status: string;
  role?: string;
}

export interface SequelSeriesStateSystem {
  name: string;
  status?: string;
}

export interface SequelSeriesStateFaction {
  name: string;
  status?: string;
}

export interface SequelSeriesState {
  sourceProjectId: string;
  sourceVolumeNumber: number;
  currentEraName: string | null;
  activeCharacters: SequelSeriesStateCharacter[];
  deadOrDepartedCharacters: SequelSeriesStateCharacter[];
  activeSystems: SequelSeriesStateSystem[];
  resolvedSystems: SequelSeriesStateSystem[];
  activeFactions: SequelSeriesStateFaction[];
  unresolvedThreads: string[];
  permanentConsequences: string[];
  currentLocations: string[];
  endingPattern: string | null;
  canonLocks: string[];
  loadStatus: SequelSeriesStateLoadStatus;
  warnings: string[];
}

export interface StorySeed {
  title: string;
  genre: WeightedGenre[];
  contentBoundaries?: ContentBoundaries;
  premise: NarrativePremise;
  characters: string;
  worldLore: string;
  style: string;
  mode: StoryMode;
  targetChapters: number;
  chapterLength: ChapterLength;
  premadeRoadmap?: string;
}

export type SequelOperationStatus =
  | 'PENDING'
  | 'EVOLVING_LEDGER'
  | 'EVOLVING_BIBLE'
  | 'READY'
  | 'FAILED'
  | 'UNKNOWN_OUTCOME'
  | 'CANCEL_REQUESTED';

export type SequelCompletedStep = 'ledger_evolved' | 'bible_evolved';

export interface SequelOperation {
  operationId: string;
  sourceProjectId: string;
  targetVolume: number;
  targetProjectId: string;
  seriesId: string;
  status: SequelOperationStatus;
  completedSteps: SequelCompletedStep[];
  createdAt: string;
  updatedAt: string;
  errorCode?: string;
  requestIds?: string[];
  resultRefs?: {
    evolvedLedgerPath?: string;
    evolvedBiblePath?: string;
  };
  evolvedLedger?: Ledger;
  evolvedBible?: Partial<StoryBible>;
}

export interface GenerationErrorBody {
  error: string;
  code: string;
  agent?: string;
  operation?: string;
  requestId: string;
  retryable: boolean;
  outcomeKnown: boolean;
}

export interface ProjectMetadata {
  id: string;
  seriesId?: string;
  canonId?: string;
  previousVolumeId?: string;
  sourceProjectId?: string;
  sequelOperationId?: string;
  sequelSetupStatus?: 'INITIALIZING' | 'READY' | 'CANCEL_REQUESTED';
  title: string;
  seriesTitle?: string;
  volumeTitle?: string;
  authorName?: string;
  subtitle?: string;
  endingType?: string;
  genre: string;
  lastUpdated: string;
  chapterCount: number;
  trustedCompletedChapterCount?: number;
  targetChapterCount?: number;
  mode: StoryMode;
  volume: number;
  status?: AppStatus;
  userId?: string;
  creditTier?: 'free_preview' | 'short_manuscript' | 'standard_manuscript' | 'large_manuscript' | 'epic_manuscript';
  rewritesUsed?: number;
  maxChaptersAllowed?: number;
  selectedChapterCount?: number;
  rewriteCreditsIncluded?: number;
  paidEntitlementId?: string;
  entitlementAppliedAt?: string;
  exportUnlocked?: boolean;
  hasUnsyncedEdits?: boolean;
  lastCanonSyncAt?: string | null;
  canonSource?: "generated" | "edited";
  finalManuscriptFingerprint?: string | null;
  activeRunId?: string | null;
  activeChapterNum?: number | null;
  generationStartedAt?: string | null;
  generationStatus?: "idle" | "running" | "saving" | "failed" | "completed";
  lastGenerationHeartbeatAt?: string | null;
}

export interface PublicationSettings {
  bookTitle: string;
  authorName: string;
  copyrightYear: string;
  publisherName?: string;
  dedication?: string;
  acknowledgments?: string;
  aboutAuthor?: string;
  alsoBy?: string;
  exportFormat: 'TXT' | 'MD' | 'DOCX';
  headingStyle: 'STANDARD' | 'CENTERED' | 'BOLD';
  paragraphStyle: 'INDENTED' | 'SPACED';
  sceneBreakStyle: 'ASTERISKS' | 'LINE' | 'SPACE';
  includeTitlePage: boolean;
  includeCopyrightPage: boolean;
  includeToc: boolean;
  includeDedication: boolean;
  includeAcknowledgments: boolean;
  includeAboutAuthor: boolean;
  includeAlsoBy: boolean;
}

export interface ExportRecord {
  id: string;
  projectId: string;
  format: string;
  fileName: string;
  storagePath: string;
  downloadUrl?: string; // added to make download easy
  createdAt: string;
  chapterCount: number;
  status: 'SUCCESS' | 'FAILED';
  errorMessage?: string;
}

export interface FullProjectState {
  id?: string;
  seriesId?: string;
  volume?: number;
  ledger: Ledger;
  storyBible: StoryBible;
  chapters: Chapter[];
  currentChapterNum: number;
  logs: LogEntry[];
  ledgerHistory?: Ledger[];
  roadmapVersions?: RoadmapVersion[];
  seriesCanon?: SeriesCanon;
  pausedRecoveryMetadata?: Record<string, unknown> | null;
}

export interface SeriesCanon {
  seriesId: string;
  globalWorldRules: string[];
  keyCharactersRegistry: Record<string, {
    name: string;
    isKeyAcrossSeries: boolean;
    initialStatus: string;
    originBook: number;
  }>;
  timeline: SeriesTimelineEvent[];
}

export interface SeriesTimelineEvent {
  id: string;
  bookVolume: number;
  chapterNumber: number;
  eventDescription: string;
  charactersInvolved: string[];
}

export interface LedgerPatch {
  chapterId: number;
  charactersDelta?: Record<string, any>;
  locationsDelta?: Record<string, any>;
  plotPointsIntroduced?: string[];
  plotPointsResolved?: string[];
  notes: string;
}

export interface ContinuityTelemetry {
  chapterNumber: number;
  requestId: string;
  proseWordCount: number;
  pacingDensity: number;
  validationWarningCount: number;
  warningsList: string[];
  timestamp: string;
}


export type ProgressionSeverity = "warning" | "repair_required" | "blocking";

export interface NarrativeStateDelta {
  fact_changes: string[];
  emotional_shifts: string[];
  new_unresolved_consequences: string[];
  resolved_consequences: string[];
}

export interface LatestStateDeltaChange {
  id: string;
  summary: string;
  changeType: 'introduced' | 'updated' | 'resolved' | 'removed' | 'transferred';
  sourceChapter?: number;
}

export interface LatestStateDelta {
  chapterNumber: number;
  chapterRevisionId?: string;
  characterChanges: LatestStateDeltaChange[];
  relationshipChanges: LatestStateDeltaChange[];
  locationChanges: LatestStateDeltaChange[];
  inventoryChanges: LatestStateDeltaChange[];
  physicalObjectChanges: LatestStateDeltaChange[];
  knowledgeChanges: LatestStateDeltaChange[];
  plotThreadChanges: LatestStateDeltaChange[];
  obligationsOpened: LatestStateDeltaChange[];
  obligationsResolved: LatestStateDeltaChange[];
  consequences: string[];
  generatedAt: string;
}

export type ContinuityEngineVersion = 'LEGACY_FILTERED' | 'AUTHORITATIVE_MERGED_V2';

export interface AuthoritativeContinuitySnapshotSectionCounts {
  characters: number;
  locations: number;
  inventory: number;
  plotThreads: number;
  obligations: number;
  physicalObjects: number;
  consequences: number;
}

export interface AuthoritativeContinuitySnapshot {
  chapterNumber: number;
  snapshotVersion: string;
  snapshotHash: string;
  sourceLedgerVersion: string;
  latestDeltaChapter: number;
  sectionCounts: AuthoritativeContinuitySnapshotSectionCounts;
  requiredItemCount: number;
  compressedItemCount: number;
  omittedOptionalItemCount: number;
  sections: {
    authoritativeStoryState: string;
    latestStateDelta: string;
    priorChapterConsequences: string;
    openContinuityObligations: string;
    currentRelevantPhysicalState: string;
    activeCharacterStates: string;
    currentAndRecentLocations: string;
    relevantInventoryAndEquipment: string;
    activePlotThreads: string;
    canonLocks: string;
    characterKnowledgeLimits: string;
  };
  promptText: string;
}

export class AuthoritativeContinuitySnapshotBudgetError extends Error {
  readonly chapterNumber: number;
  readonly requiredChars: number;
  readonly budgetChars: number;

  constructor(params: { chapterNumber: number; requiredChars: number; budgetChars: number }) {
    super(
      `Priority-1 continuity snapshot exceeds safe budget (${params.requiredChars} > ${params.budgetChars} chars) for chapter ${params.chapterNumber}`,
    );
    this.name = 'AuthoritativeContinuitySnapshotBudgetError';
    this.chapterNumber = params.chapterNumber;
    this.requiredChars = params.requiredChars;
    this.budgetChars = params.budgetChars;
  }
}

export interface DensityPrecheck {
  paragraphCount: number;
  dialogueParagraphCount: number;
  longestLowMovementRun: number;
  repeatedMeasurements: string[];
  repeatedSensorySignals: string[];
  repeatedEquipmentSignals: string[];
  repeatedBeliefStatements: string[];
  candidateDescriptiveParagraphIndexes: number[];
}

export interface ProgressionInspectionResult {
  passed: boolean;
  severity: ProgressionSeverity;
  reason: string;
  feedback_for_scribe: string;
  stateDelta: NarrativeStateDelta;
}

export interface FinalDeltaVerification {
  isValid: boolean;
  reason: string;
  authoritativeDelta: NarrativeStateDelta;
}

export interface ChapterProgressionContract {
  roadmapMilestone: string;
  requiredPressure: string;
}

export interface BookArcContract {
  arcName: string;
  arcSummary: string;
}

export interface WorkspaceActionState {
  kind: "PROJECT_REPAIR_REQUIRED" | "GENERATION_RUNNING" | "BOOK_COMPLETE" | "BOOK_CREDIT_REQUIRED" | "BUY_CREDIT_REQUIRED" | "WRITE_NEXT";
  buttonLabel: string;
  subLabel: string;
  disabled: boolean;
}
